/**
 * Kubernetes API client that interacts with kubectl proxy
 * 
 * This service provides methods to list and watch Kubernetes resources
 * through the kubectl proxy running on localhost:8001
 */

// Point to the CORS proxy or direct to kubectl proxy if --disable-filter=true is used
const API_SERVER = 'http://localhost:8181'; // CORS proxy
// const API_SERVER = 'http://localhost:8001'; // Direct kubectl proxy with --disable-filter=true
const API_BASE = `${API_SERVER}/api/v1`;

/**
 * Get a list of resources of a specific type
 * 
 * @param resourceType The type of resource to list (pods, nodes, etc.)
 * @param namespace The namespace to filter by (empty for cluster-scoped resources)
 * @returns Promise with the list of resources
 */
export async function listResource(resourceType: string, namespace = '') {
  const url = namespace 
    ? `${API_BASE}/namespaces/${namespace}/${resourceType}`
    : `${API_BASE}/${resourceType}`;
  
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch ${resourceType}: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Watch a resource type for changes
 * 
 * @param resourceType The type of resource to watch
 * @param namespace The namespace to filter by (empty for cluster-scoped resources)
 * @param resourceVersion The resource version to start watching from
 * @param onEvent Callback function that's called for each event
 * @param signal AbortSignal to cancel the watch
 */
export async function watchResource(
  resourceType: string,
  namespace = '',
  resourceVersion: string, 
  onEvent: (event: any) => void,
  signal: AbortSignal
) {
  const url = namespace 
    ? `${API_BASE}/namespaces/${namespace}/${resourceType}?watch=1&resourceVersion=${resourceVersion}`
    : `${API_BASE}/${resourceType}?watch=1&resourceVersion=${resourceVersion}`;
  
  const response = await fetch(url, { signal });
  
  if (!response.ok) {
    throw new Error(`Failed to watch ${resourceType}: ${response.statusText}`);
  }
  
  const reader = response.body!.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  
  try {
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        break;
      }
      
      buffer += decoder.decode(value, { stream: true });
      
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep the last (potentially incomplete) line in the buffer
      
      for (const line of lines) {
        if (line.trim()) {
          try {
            const event = JSON.parse(line);
            onEvent(event);
          } catch (error) {
            console.error('Error parsing event:', error, line);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Stream logs from a pod
 * 
 * @param podName The name of the pod
 * @param namespace The namespace of the pod
 * @param options Optional parameters for log streaming
 * @param signal AbortSignal to cancel the stream
 * @returns ReadableStream of the pod logs
 */
export async function streamPodLogs(
  podName: string,
  namespace: string,
  options: {
    follow?: boolean;
    tailLines?: number;
    container?: string;
    previous?: boolean;
  } = {},
  signal?: AbortSignal
) {
  // Build query parameters
  const params = new URLSearchParams();
  
  if (options.follow !== undefined) {
    params.append('follow', options.follow.toString());
  }
  
  if (options.tailLines !== undefined) {
    params.append('tailLines', options.tailLines.toString());
  }
  
  if (options.container) {
    params.append('container', options.container);
  }
  
  if (options.previous) {
    params.append('previous', 'true');
  }
  
  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE}/namespaces/${namespace}/pods/${podName}/log${queryString}`;
  
  const response = await fetch(url, { signal });
  
  if (!response.ok) {
    throw new Error(`Failed to stream pod logs: ${response.statusText}`);
  }
  
  return response.body;
}

/**
 * Get logs from a pod (non-streaming)
 * 
 * @param podName The name of the pod
 * @param namespace The namespace of the pod
 * @param options Optional parameters for logs
 * @returns Promise with pod logs as text
 */
export async function getPodLogs(
  podName: string,
  namespace: string,
  options: {
    tailLines?: number;
    container?: string;
    previous?: boolean;
  } = {}
) {
  // Build query parameters
  const params = new URLSearchParams();
  
  if (options.tailLines !== undefined) {
    params.append('tailLines', options.tailLines.toString());
  }
  
  if (options.container) {
    params.append('container', options.container);
  }
  
  if (options.previous) {
    params.append('previous', 'true');
  }
  
  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE}/namespaces/${namespace}/pods/${podName}/log${queryString}`;
  
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to get pod logs: ${response.statusText}`);
  }
  
  return response.text();
}

/**
 * Get metrics for pods
 * 
 * @param namespace The namespace to filter by (empty for all namespaces)
 * @returns Promise with pod metrics
 */
export async function getPodMetrics(namespace = '') {
  const url = namespace 
    ? `${API_SERVER}/apis/metrics.k8s.io/v1beta1/namespaces/${namespace}/pods`
    : `${API_SERVER}/apis/metrics.k8s.io/v1beta1/pods`;
  
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch pod metrics: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Get metrics for nodes
 * 
 * @returns Promise with node metrics
 */
export async function getNodeMetrics() {
  const url = `${API_SERVER}/apis/metrics.k8s.io/v1beta1/nodes`;
  
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch node metrics: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Scale a deployment
 * 
 * @param namespace The namespace of the deployment
 * @param name The name of the deployment
 * @param replicas The number of replicas to scale to
 * @returns Promise with the scaled deployment
 */
export async function scaleDeployment(namespace: string, name: string, replicas: number) {
  const url = `${API_SERVER}/apis/apps/v1/namespaces/${namespace}/deployments/${name}/scale`;
  
  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json-patch+json',
    },
    body: JSON.stringify([
      {
        op: 'replace',
        path: '/spec/replicas',
        value: replicas
      }
    ])
  });
  
  if (!response.ok) {
    throw new Error(`Failed to scale deployment: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Delete a pod
 * 
 * @param namespace The namespace of the pod
 * @param name The name of the pod
 * @returns Promise with the deletion result
 */
export async function deletePod(namespace: string, name: string) {
  const url = `${API_BASE}/namespaces/${namespace}/pods/${name}`;
  
  const response = await fetch(url, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    throw new Error(`Failed to delete pod: ${response.statusText}`);
  }
  
  return response.json();
} 